import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyPreferences,
  effectivePreferences,
  readStoredPreferences,
  writeStoredPreferences,
  type Density,
  type Preferences,
  type Theme,
} from '../lib/preferences';

interface PreferencesContextValue extends Preferences {
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export interface PreferencesProviderProps {
  children: ReactNode;
  /** Instance defaults (Settings → Branding) for users without their own choice. */
  defaults?: Partial<Preferences>;
}

export function PreferencesProvider({ children, defaults }: PreferencesProviderProps) {
  const [stored, setStored] = useState<Partial<Preferences>>(readStoredPreferences);
  const theme = stored.theme ?? defaults?.theme;
  const density = stored.density ?? defaults?.density;
  // Only set fields override the built-in defaults (an undefined field must not erase them).
  const preferences = useMemo(() => effectivePreferences({}, { ...(theme && { theme }), ...(density && { density }) }), [theme, density]);

  useEffect(() => {
    applyPreferences(preferences);
    if (preferences.theme !== 'system') return;
    // Follow the OS theme live while "system" is selected.
    const query = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    const onChange = () => applyPreferences(preferences);
    query?.addEventListener('change', onChange);
    return () => query?.removeEventListener('change', onChange);
  }, [preferences]);

  const update = useCallback((change: Partial<Preferences>) => {
    setStored((current) => {
      const next = { ...current, ...change };
      writeStoredPreferences(next);
      return next;
    });
  }, []);
  const setTheme = useCallback((value: Theme) => update({ theme: value }), [update]);
  const setDensity = useCallback((value: Density) => update({ density: value }), [update]);

  const value = useMemo(() => ({ ...preferences, setTheme, setDensity }), [preferences, setTheme, setDensity]);
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

/** Current theme/density and setters. Must be used inside `PreferencesProvider`. */
export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences must be used inside <PreferencesProvider>.');
  return context;
}
